import _ from 'lodash';
import { createSelector } from 'reselect';
import Immutable from 'immutable';
import {
  getMaterialTypeCountsForStatus,
  getPresentStatusesFromSummary,
  getCountsByStatusFromSummary
} from '@bibliocommons/utils-borrowing';
import { selectBorrowingSummary } from './BorrowingSelector';
import { selectShelvesForCurrentUser } from './ShelfSelector';

const checkedoutSummary = state => selectBorrowingSummary(state, 'checkedout');
const statusFilter = (state, props) => _.get(props, 'router.params.status');
const pagination = state => state.getIn(['borrowing', 'checkedout', 'pagination'], Immutable.Map());
const fulfilmentState = state => state.getIn(['borrowing', 'checkedout', 'fulfilment'], Immutable.Map());
const allCheckouts = state => state.getIn(['entities', 'checkouts'], Immutable.Map());
const bibsById = state => state.getIn(['entities', 'bibs'], Immutable.Map());
const checkoutTransactions = state => state.getIn(['borrowing', 'checkedout', 'checkoutTransactions'], Immutable.Map());
const checkedoutStatus = state => state.getIn(['borrowing', 'checkedout', 'summary', 'status'], Immutable.Map());
const failuresByAction = state => state.getIn(['borrowing', 'checkedout', 'failures'], Immutable.Map());
const currentItemIds = state => state.getIn(['borrowing', 'checkedout', 'items'], Immutable.List());
const currentView = state => state.getIn(['borrowing', 'checkedout', 'view']);
const fetchingState = state => state.getIn(['borrowing', 'checkedout', 'fetching'], false);

export const selectCheckoutsByMetadataId = createSelector([allCheckouts], checkoutsById =>
  checkoutsById.groupBy(checkout => checkout.get('metadataId'))
);

export const selectCurrentCheckouts = createSelector(
  [currentItemIds, allCheckouts, bibsById, selectShelvesForCurrentUser],
  (checkoutIds, checkoutsById, bibs, shelfItems) =>
    checkoutIds
      .map(id => checkoutsById.get(id))
      .filter(checkout => !!checkout)
      .map(checkout => checkout.set('bib', bibs.get(checkout.get('metadataId'))))
      .map(checkout => {
        const isAlreadyOnShelf = shelfItems.some(item => item.get('metadataId') === checkout.get('metadataId'));

        const canAddToShelf = checkout.get('bib') && !isAlreadyOnShelf;

        const actions = canAddToShelf
          ? checkout.get('actions', Immutable.List()).push('addToShelf')
          : checkout.get('actions');

        return checkout.set('actions', actions);
      })
      .toList()
);

export const selectCountsByStatus = createSelector([checkedoutSummary], summary =>
  getCountsByStatusFromSummary(summary)
);

export const selectPagination = createSelector([pagination], page => page);

export const selectMaterialTypeCounts = createSelector([checkedoutSummary, statusFilter], (summary, currentStatus) =>
  getMaterialTypeCountsForStatus(summary, currentStatus)
);

export const selectPresentStatuses = createSelector([checkedoutSummary], summary =>
  getPresentStatusesFromSummary(summary)
);

export const selectCheckedoutQuotas = createSelector([checkedoutSummary], summary =>
  summary.get('quotas', Immutable.List())
);

export const selectSelectedStatus = createSelector([statusFilter], status => (status ? status.toUpperCase() : status));

export const selectCheckoutTransactions = createSelector([checkoutTransactions], transactions => transactions);

export const selectCheckedoutStatus = createSelector([checkedoutStatus], status => status);

export const selectCurrentView = createSelector([currentView], view => view);

export const selectFetching = createSelector([fetchingState], fetching => fetching);

export const selectFailuresByAction = createSelector([failuresByAction], failures => failures);

export const selectRenewFailures = createSelector(
  [failuresByAction, bibsById, allCheckouts],
  (failures, bibs, checkouts) =>
    failures
      .get('renew', Immutable.List())
      .map(failure => failure.set('checkout', checkouts.get(failure.get('id'))))
      .map(failure => failure.set('bib', bibs.get(failure.getIn(['checkout', 'metadataId']))))
);

export const selectFulfilmentData = createSelector([fulfilmentState], fulfilment => fulfilment);

export default {
  selectMaterialTypeCounts,
  selectPresentStatuses,
  selectCheckedoutQuotas,
  selectSelectedStatus,
  selectCheckoutsByMetadataId,
  selectPagination,
  selectCurrentView,
  selectFetching,
  selectFulfilmentData,
  selectCheckoutTransactions,
  selectCheckedoutStatus,
  selectFailuresByAction,
  selectRenewFailures,
  selectCurrentCheckouts,
  selectCountsByStatus
};
